'use client';

import { useState, type RefObject } from 'react';
import CmsImage from '@/components/ui/CmsImage';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import type { PncCardSection } from '@/types/people-and-culture';
import { formatPncStatValue } from '../lib/format-pnc-stat';
import AllInitiativesModal from './AllInitiativesModal';

interface CommitmentsSectionProps {
  sectionRef: RefObject<HTMLDivElement>;
  zone: PncCardSection;
}

function isAccentColor(color: string | null | undefined): boolean {
  if (!color) return false;
  const normalized = color.trim().toUpperCase();
  return normalized === '#EC1C24' || normalized === '#ED1C22' || normalized === '#E31E24';
}

export default function CommitmentsSection({ sectionRef, zone }: CommitmentsSectionProps) {
  const [showInitiativesModal, setShowInitiativesModal] = useState(false);
  const cards = zone.cardItem ?? [];
  const initiativeModal = zone.initiativeModal?.[0];
  const hasInitiatives = Boolean(initiativeModal?.initiativeList?.length);
  if (cards.length === 0) return null;

  return (
    <section ref={sectionRef} className="bg-white px-4 sm:px-6 md:px-16 lg:px-24">
      <div
        data-reveal
        className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-start sm:justify-between"
      >
        <h2 className="text-3xl font-bold leading-tight text-[#0a0a0a] sm:text-4xl md:text-5xl">
          {zone.heading?.length
            ? zone.heading.map((part, index) => (
                <span
                  key={part.id ?? index}
                  style={{
                    color: isAccentColor(part.textColor) ? '#ed1c22' : part.textColor || '#0a0a0a',
                  }}
                >
                  {part.text}
                  {index < zone.heading.length - 1 ? ' ' : ''}
                </span>
              ))
            : zone.sectionTitile}
        </h2>
        {initiativeModal?.buttonText && hasInitiatives ? (
          <SwapTextButton
            text={initiativeModal.buttonText}
            hoverText={initiativeModal.buttonText}
            variant="outline"
            aria-label={initiativeModal.buttonText}
            onClick={() => setShowInitiativesModal(true)}
            className="shrink-0 self-start !rounded-full !px-6 sm:mt-1"
          />
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
        {cards.map((commitment) => {
          const imageUrl = getStrapiMediaUrl(commitment.media, 'medium');
          const stat = commitment.stats?.[0];

          return (
            <div
              key={commitment.id}
              data-commit-card
              className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-lg"
            >
              <div className="relative h-44 w-full overflow-hidden sm:h-48">
                <CmsImage
                  src={imageUrl}
                  alt={commitment.cardTitle ?? ''}
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  fallbackSrc={MEDIA_PLACEHOLDER}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                {commitment.pillText ? (
                  <span className="absolute left-3 top-3 rounded-full bg-black/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
                    {commitment.pillText}
                  </span>
                ) : null}
              </div>
              <div className="p-5">
                <h3 className="mb-2 text-base font-bold text-[#0a0a0a]">{commitment.cardTitle}</h3>
                <p className="text-xs leading-relaxed text-gray-500 min-h-[80px] max-sm:min-h-0">
                  {commitment.description ? stripHtml(commitment.description) : ''}
                </p>
                {stat ? (
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <p className="text-2xl font-bold text-[#ed1c22]">{formatPncStatValue(stat)}</p>
                    <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                      {stat.statheading}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <AllInitiativesModal
        isOpen={showInitiativesModal}
        onClose={() => setShowInitiativesModal(false)}
        content={initiativeModal}
      />
    </section>
  );
}
